import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const MARGIN = { top: 20, right: 20, bottom: 60, left: 60 };

const FIELDS = [
  { key: 'pest365', label: 'Pest365', color: '#2563eb' },
  { key: 'pest365Plus', label: 'Pest365+', color: '#8b5cf6' },
  { key: 'oneTime', label: 'One-Time', color: '#f59e0b' },
  { key: 'initialServiceVisit', label: 'Init. Visit', color: '#16a34a' },
  { key: 'mosquitoTickControl', label: 'Mosquito/Tick', color: '#dc2626' },
];

const SORT_OPTIONS = [
  { value: 'state', label: 'State' },
  { value: 'pest365', label: 'Pest365' },
  { value: 'total', label: 'Total' },
  { value: 'addressCount', label: 'Address Count' },
];

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '4px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '4px',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '16px',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: 500,
    userSelect: 'none',
  },
  legendSwatch: {
    width: '14px',
    height: '14px',
    borderRadius: '2px',
  },
  container: {
    overflowX: 'auto',
    minWidth: '600px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  sortContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    color: '#6b7280',
  },
  sortSelect: {
    fontSize: '12px',
    padding: '4px 8px',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    background: '#fff',
    color: '#374151',
    cursor: 'pointer',
    outline: 'none',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function FieldComparisonBar({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [hiddenFields, setHiddenFields] = useState(new Set());
  const [sortBy, setSortBy] = useState('state');

  const toggleField = (key) => {
    setHiddenFields((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const visibleFields = useMemo(() => {
    return FIELDS.filter((f) => !hiddenFields.has(f.key));
  }, [hiddenFields]);

  const sortedData = useMemo(() => {
    if (!data || data.length === 0) return [];
    const sorted = [...data];
    switch (sortBy) {
      case 'state':
        sorted.sort((a, b) => a.state.localeCompare(b.state));
        break;
      case 'pest365':
        sorted.sort((a, b) => (b.pest365 || 0) - (a.pest365 || 0));
        break;
      case 'total': {
        const total = (d) =>
          (d.pest365 || 0) +
          (d.pest365Plus || 0) +
          (d.oneTime || 0) +
          (d.initialServiceVisit || 0) +
          (typeof d.mosquitoTickControl === 'number' ? d.mosquitoTickControl : 0);
        sorted.sort((a, b) => total(b) - total(a));
        break;
      }
      case 'addressCount':
        sorted.sort((a, b) => (b.addressCount || 0) - (a.addressCount || 0));
        break;
      default:
        break;
    }
    return sorted;
  }, [data, sortBy]);

  useEffect(() => {
    if (!sortedData || sortedData.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = containerRef.current
      ? containerRef.current.clientWidth
      : 700;
    const width = Math.max(600, containerWidth);
    const height = 380;
    const innerW = width - MARGIN.left - MARGIN.right;
    const innerH = height - MARGIN.top - MARGIN.bottom;

    svg.attr('width', width).attr('height', height);

    const g = svg
      .append('g')
      .attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

    // Scales
    const states = sortedData.map((d) => d.state);
    const fieldKeys = visibleFields.map((f) => f.key);

    const x0 = d3
      .scaleBand()
      .domain(states)
      .range([0, innerW])
      .paddingInner(0.2)
      .paddingOuter(0.1);

    const x1 = d3
      .scaleBand()
      .domain(fieldKeys)
      .range([0, x0.bandwidth()])
      .padding(0.05);

    // Compute Y domain from visible fields
    let yMax = 0;
    sortedData.forEach((d) => {
      visibleFields.forEach((field) => {
        const val = d[field.key];
        if (typeof val === 'number' && val > yMax) {
          yMax = val;
        }
      });
    });
    yMax = yMax || 100;
    const yPadding = yMax * 0.1;

    const y = d3
      .scaleLinear()
      .domain([0, yMax + yPadding])
      .nice()
      .range([innerH, 0]);

    // Build color map
    const colorMap = {};
    FIELDS.forEach((f) => {
      colorMap[f.key] = f.color;
    });

    // Grid lines - horizontal
    g.selectAll('.grid-h')
      .data(y.ticks())
      .enter()
      .append('line')
      .attr('class', 'grid-h')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x0).tickSize(0))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('dy', '0.8em');

    // Y axis
    g.append('g')
      .call(
        d3
          .axisLeft(y)
          .ticks(6)
          .tickSize(0)
          .tickFormat((d) => `$${d}`)
      )
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    // Address count labels below state names
    g.selectAll('.addr-count')
      .data(sortedData)
      .enter()
      .append('text')
      .attr('class', 'addr-count')
      .attr('x', (d) => x0(d.state) + x0.bandwidth() / 2)
      .attr('y', innerH + 32)
      .attr('text-anchor', 'middle')
      .attr('font-size', '9px')
      .attr('fill', '#9ca3af')
      .text((d) => `(${d.addressCount} addr)`);

    // Bars
    const stateGroups = g
      .selectAll('.state-group')
      .data(sortedData)
      .enter()
      .append('g')
      .attr('class', 'state-group')
      .attr('transform', (d) => `translate(${x0(d.state)}, 0)`);

    stateGroups.each(function (stateData) {
      const group = d3.select(this);

      visibleFields.forEach((field) => {
        const val = stateData[field.key];
        if (val == null || typeof val !== 'number') return;

        const barX = x1(field.key);
        const barY = y(val);
        const barW = x1.bandwidth();
        const barH = innerH - barY;

        if (barW <= 0 || barH <= 0) return;

        group
          .append('rect')
          .attr('x', barX)
          .attr('y', barY)
          .attr('width', barW)
          .attr('height', barH)
          .attr('fill', colorMap[field.key])
          .attr('rx', 1)
          .attr('ry', 1)
          .style('cursor', 'pointer')
          .on('mousemove', (event) => {
            d3.select(event.currentTarget).attr(
              'fill',
              d3.color(colorMap[field.key]).darker(0.3).toString()
            );
            setTooltip({
              x: event.clientX + 12,
              y: event.clientY - 10,
              state: stateData.state,
              addressCount: stateData.addressCount,
              entries: FIELDS.map((f) => ({
                label: f.label,
                color: f.color,
                value: stateData[f.key],
              })),
            });
          })
          .on('mouseleave', (event) => {
            d3.select(event.currentTarget).attr('fill', colorMap[field.key]);
            setTooltip(null);
          });
      });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [sortedData, visibleFields]);

  if (!data || data.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.title}>State Price Breakdown</div>
        <div style={styles.subtitle}>Average price per field by state</div>
        <div
          style={{
            color: '#6b7280',
            textAlign: 'center',
            padding: '40px 0',
          }}
        >
          No state breakdown data available
        </div>
      </div>
    );
  }

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div>
          <div style={styles.title}>State Price Breakdown</div>
        </div>
        <div style={styles.sortContainer}>
          <span>Sort by:</span>
          <select
            style={styles.sortSelect}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div style={styles.subtitle}>Average price per field by state</div>
      <div style={styles.legend}>
        {FIELDS.map((field) => {
          const hidden = hiddenFields.has(field.key);
          return (
            <div
              key={field.key}
              style={styles.legendItem}
              onClick={() => toggleField(field.key)}
            >
              <span
                style={{
                  ...styles.legendSwatch,
                  background: hidden ? '#d1d5db' : field.color,
                }}
              />
              <span
                style={{
                  color: hidden ? '#d1d5db' : '#374151',
                  textDecoration: hidden ? 'line-through' : 'none',
                }}
              >
                {field.label}
              </span>
            </div>
          );
        })}
      </div>
      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              color: '#1f2937',
              marginBottom: '4px',
            }}
          >
            {tooltip.state}
          </div>
          <div
            style={{
              fontSize: '11px',
              color: '#9ca3af',
              marginBottom: '6px',
            }}
          >
            {tooltip.addressCount} address{tooltip.addressCount !== 1 ? 'es' : ''}
          </div>
          {tooltip.entries.map((entry) => (
            <div
              key={entry.label}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                gap: '16px',
                marginBottom: '2px',
              }}
            >
              <span style={{ color: entry.color }}>{entry.label}</span>
              <span style={{ fontWeight: 600 }}>
                {entry.value != null && typeof entry.value === 'number'
                  ? `$${entry.value.toFixed(2)}`
                  : entry.value === 'not_available'
                  ? 'N/A'
                  : '--'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
